if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      static cartMutationQueue = Promise.resolve();

      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        this.addEventListener('click', (event) => {
          const addButton = event.target.closest('.bb-add-to-basket');
          if (!addButton || !this.contains(addButton) || addButton.disabled) return;

          const input = this.input;
          if (!input) return;

          const minimum = Math.max(parseInt(this.dataset.min, 10) || 1, 1);
          const previousValue = parseInt(input.value, 10) || 0;
          const quantityContainer = this.querySelector('.bb-card-quantity');

          this.optimisticAddPending = true;
          this.optimisticPreviousQuantity = previousValue;

          addButton.disabled = true;
          addButton.setAttribute('aria-busy', 'true');
          addButton.classList.add('hidden');
          quantityContainer?.classList.remove('hidden');
          this.setAttribute('aria-busy', 'true');
          if (this.statusElement) {
            this.statusElement.textContent = `Adding ${this.dataset.productTitle || 'product'} to basket.`;
          }

          input.value = minimum;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.closest('[data-bb-related-products]') && Array.isArray(event.cartData?.items)) {
            this.syncControlsFromCart(event.cartData);
            this.listenForActiveInput();
            this.listenForKeydown();
            return;
          }

          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      get statusElement() {
        return this.querySelector('.bb-quick-add-status');
      }

      syncControlsFromCart(cartData) {
        if (!Array.isArray(cartData?.items)) return;

        const variantId = parseInt(this.dataset.index, 10);
        const cartLine = cartData.items.find((item) => item.id === variantId);
        const quantity = cartLine?.quantity || 0;
        const addButton = this.querySelector('.bb-add-to-basket');
        const quantityContainer = this.querySelector('.bb-card-quantity');
        const input = this.input;

        if (input) {
          input.value = quantity;
          input.dataset.cartQuantity = quantity;
        }

        addButton?.classList.toggle('hidden', quantity > 0);
        quantityContainer?.classList.toggle('hidden', quantity === 0);
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              if (this.statusElement) this.statusElement.textContent = 'Unable to refresh the basket. Please try again.';
              this.removeAttribute('aria-busy');
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const linesUpdate = this.startCartLinesUpdate(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        const runUpdate = () =>
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
            .then((response) => response.json())
            .then((parsedState) => {
              if (parsedState.errors) {
                throw Object.assign(new Error(parsedState.errors), { code: 'INVALID' });
              }

              linesUpdate?.resolve(parsedState);
              this.renderSections(parsedState, ids);
              this.optimisticAddPending = false;
              this.optimisticPreviousQuantity = null;
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
            })
            .catch((e) => {
              if (e.code !== 'INVALID') console.error(e);
              this.dispatchCartErrorEvent(
                e.code === 'INVALID' ? e.message : window.cartStrings.error,
                e.code || 'SERVICE_UNAVAILABLE'
              );
              linesUpdate?.reject(e);
              this.rollbackOptimisticAdd();
              if (this.statusElement) this.statusElement.textContent = 'Unable to update the basket. Please try again.';
              // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
              // e.target.setCustomValidity(error);
              // e.target.reportValidity();
              // this.resetQuantityInput(ids[index]);
              // this.selectProgressBar().classList.add('hidden');
              // e.target.select();
              // this.cleanErrorMessageOnType(e);
            })
            .finally(() => {
              this.selectProgressBar()?.classList.add('hidden');
              this.setRequestStarted(false);
              this.removeAttribute('aria-busy');
            });

        // Each product card has its own BulkAdd request queue. Serializing the
        // actual cart mutations across all cards prevents two rapid clicks on
        // different products from racing and rendering stale cart state.
        QuickAddBulk.cartMutationQueue = QuickAddBulk.cartMutationQueue.then(runUpdate, runUpdate);
        return QuickAddBulk.cartMutationQueue;
      }

      rollbackOptimisticAdd() {
        if (!this.optimisticAddPending) return;

        const previousQuantity = parseInt(this.optimisticPreviousQuantity, 10) || 0;
        const input = this.input;
        const addButton = this.querySelector('.bb-add-to-basket');
        const quantityContainer = this.querySelector('.bb-card-quantity');

        if (input) {
          input.value = previousQuantity;
          input.setAttribute('value', previousQuantity);
        }

        addButton?.classList.toggle('hidden', previousQuantity > 0);
        if (addButton) {
          addButton.disabled = false;
          addButton.removeAttribute('aria-busy');
        }
        quantityContainer?.classList.toggle('hidden', previousQuantity === 0);

        this.optimisticAddPending = false;
        this.optimisticPreviousQuantity = null;
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          const sectionHtml = parsedState.sections?.[section.section];
          if (!sectionHtml) return;

          if (section.section === this.sectionId) {
            const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
            if (!parsedSection.querySelector(section.selector)) {
              this.syncControlsFromCart(parsedState);
              return;
            }
          }

          if (section.section === 'cart-drawer') {
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        if (this.statusElement) this.statusElement.textContent = 'Basket updated.';
        this.removeAttribute('aria-busy');
        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
